import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { InfoItem } from 'components'
import DataGrid, { ColumnDataGrid } from 'components/DataGrid'
import Icon from 'components/Icon'
import { TopToolbar } from 'components/lists'
import {
    PosterOrder,
    PosterType
} from 'generated/graphql'
import { useEventSlug, useSlug, useDeleteItem } from 'hooks'
import NoImage from 'assets/no-image.jpg'
import { _ } from 'utils'
import { addModal } from 'features/modals/modalSlice'
import CreatePoster from '../components/CreatePoster'
import UpdatePoster from '../components/UpdatePoster'
import { deletePoster } from '../graphql/mutation'
import { usePosterItems } from '../hooks'
import { Pagination } from '../../pagination'
import { handleSubmit, importProps } from '../importConfig'

const S = {
    Wrapper: styled.div`
        width: 100%;
        margin-top: -28px;
        padding: 0 10px;
    `,
}

const Posters = ({ posterSpaceId, enableChat } : { posterSpaceId: number, enableChat: boolean }) => {
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState(PosterOrder.TitleAsc)
    const eventSlug = useEventSlug()
    const posterSlug = useSlug('posterSlug')
    const dispatch = useDispatch()

    const {
        list, requestStatus, pagination, totalItems, handlePageChange, fetchData
    } = usePosterItems({
        eventId: eventSlug, search, sort, filters: { parent: { slug: posterSlug } }
    })

    const showCreateModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreatePoster
                    close={close}
                    callGetPosters={fetchData}
                    spaceId={posterSpaceId}
                    enableChat={enableChat}
                />
            ),
            header: _('cinema.create_movie.modal_header'),
        }))
    }

    const showUpdateModal = (poster: PosterType) => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <UpdatePoster
                    close={close}
                    callGetPosters={fetchData}
                    poster={poster}
                    enableChat={enableChat}
                />
            ),
            header: _('cinema.create_movie.modal_header'),
        }))
    }

    const deleteFn = (id) => deletePoster(id)
    const { showDeleteConfirmModal } = useDeleteItem(deleteFn, fetchData)

    const columns: ColumnDataGrid[] = useMemo(() => [
        {
            name: 'image',
            width: 11,
            minWidth: 60,
            render: (row: PosterType) => (
                <InfoItem
                    image={row.preview || NoImage}
                    name={row.title}
                    description={row.authorNames}
                    squareImage
                    onClick={() => showUpdateModal(row)}
                />
            )
        },
        {
            name: 'actions',
            width: 1,
            render: (row: PosterType) => (
                <>
                    <Icon icon="edit" size={18} onClick={() => showUpdateModal(row)} className="mr-2" />
                    <Icon icon="trash" size={18} onClick={() => showDeleteConfirmModal(row.id)} />
                </>
            )
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [])

    return (
        <S.Wrapper>
            <TopToolbar
                addItemText={_('poster.list.add_item_button')}
                onAddItem={showCreateModal}
                onSearch={setSearch}
                searchPlaceHolder={_('poster.list.search_placeholder')}
                sortOptions={[
                    { label: _('poster.form.sort_by.title_asc'), value: PosterOrder.TitleAsc },
                    { label: _('poster.form.sort_by.title_desc'), value: PosterOrder.TitleDesc },
                    { label: _('poster.form.sort_by.authorname_asc'), value: PosterOrder.AuthorNamesAsc },
                    { label: _('poster.form.sort_by.authorname_desc'), value: PosterOrder.AuthorNamesDesc },
                ]}
                onSort={setSort}
                bulkActions={{
                    importProps: {
                        ...importProps,
                        onSubmit: (data) => handleSubmit(posterSpaceId, data, dispatch, fetchData)
                    }
                }}
            />
            <>
                <DataGrid
                    columns={columns}
                    data={list}
                    hideAction
                    requestStatus={requestStatus}
                />
                <Pagination
                    page={pagination?.page}
                    onPageChange={handlePageChange}
                    totalItems={totalItems}
                    disabled={requestStatus === 'loading'}
                />
            </>
        </S.Wrapper>
    )
}

export default Posters
